import {
  BayLcgVcgTcgAndPairings,
  BaySizesAndCgs,
  IBayPattern,
  ICellCGsInfo,
  IGetPairedBaysOutput,
  IReturnTypeGetMeasuresFromAttrs,
  IRowPattern,
  TGetContainersCgsResult,
  convertContainerExtWCgsToCellInfo,
  generateCoordFunctions,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  BayLevelEnum,
  CraneSideEnum,
  ForeAftEnum,
  IBayLevelData,
  ISizeSummary,
  IVesselParts,
  VesselPartTypeEnum,
} from "open-vessel-definition";
import { feetToMillimeters, roundDec } from "@tedivo/tedivo-pure-helpers";

import { IBayLevelOffsetBottomBase } from "open-vessel-definition";
import { IGenerateContainerSymbolsProps } from "../../types/IGenerateContainerSymbolsProps";
import { TContainersSymbolsConfig } from "../../types/TContainersSymbolsConfig";
import { colorShade } from "../helpers/colorShade";
import { createSvgTopBayNumbers } from "../helpers/createSvgTopBayNumbers";
import { generateContainerSymbols } from "../helpers/generateContainerSymbols";
import { obtainSideViewFilterFn } from "./filterFns/obtainSideViewFilterFn";

export const SVG_WIDTH = 1500;
export const DISTANCE_TO_CONTAINER_FOR_SHIP_LINE = 4000;

export function useSimpleSideView<T>({
  sizeSummary,
  baysData,
  lcgVcgTcgAndPairings,
  symbolsOptions,
  vesselPartsData = [],
  containersWithPositions,
  selectContainerCriteria,
  svgElement,
  svgElementMeasures,
  bayNumbersDoubleClickAction,
  containersSymbolsConfig,
  overridenHeight,
}: ICreateSimpleSideViewProps<T>): {
  sideViewSvg: SVGElement;
  missingImportantXcgs: boolean;
  xRange: number;
  yRange: number;
  xPos: (lcg: number, d?: number) => number;
  yPos: (vcg: number, d?: number) => number;
  svgSizeRatio: number;
  svgBayLevelGroups: ISvgGroup[];
  symbols: Record<string, SVGElement>;
} {
  const {
    bayLevelPositionsAbove,
    bayLevelPositionsBelow,
    missingImportantVcgs,
    missingImportantLcgs,
    missingImportantTcgs,
    cgStats: { totalSlotsCount },
    lcgsBy20Bay,
    pairedBaysCalc: pairedBays,
    maxHeight,
  } = lcgVcgTcgAndPairings;

  const missingImportantXcgs = missingImportantTcgs || missingImportantLcgs || missingImportantVcgs;

  // 1. Add missing LCGs and VCGs if needed, return x,y functions
  const coordFns = generateCoordFunctions({
    sizeSummary,
    baysData,
    vesselPartsData,
    bayLcvVcgTcgAndPairings: lcgVcgTcgAndPairings,
    maxHeightAbsolute: containersWithPositions.maxHeightAbsolute,
    svgElementMeasures,
  });

  if (!coordFns) {
    throw new Error("Could not generate coord functions");
  }

  const {
    xRange,
    yRange,
    xPos,
    yPos,
    scaled,
    oneInchScaled,
    allPartsLengthInMeters,
    svgSizeRatio,
    vOneBaySlots,
  } = coordFns;

  const { generalMedianVcg: midVcg, midVcgByBay, offsetBottomBasesDict } = containersWithPositions;

  // 2. SVG Node
  const svgNode = svgElement.cloneNode(true) as SVGSVGElement;
  svgNode.setAttribute("data-rnd", Math.random().toString(36).slice(2, 7));

  if (xRange) svgNode.setAttribute("width", String(roundDec(xRange, 0)));
  if (yRange) svgNode.setAttribute("height", String(roundDec(yRange, 0)));
  if (xRange && yRange)
    svgNode.setAttribute("viewBox", `0 0 ${roundDec(xRange, 0)} ${roundDec(yRange, 0)}`);

  svgNode.setAttribute("data-scale", String(roundDec(svgSizeRatio, 5)));
  svgNode.setAttribute("data-one-feet-scaled", String(oneInchScaled));
  svgNode.setAttribute("data-length-meters", String(allPartsLengthInMeters));

  const svgSideViewNode = svgNode.querySelector("#SideView") as SVGElement | null;
  const svgShellNode = svgNode.querySelector("#ShipBody") as SVGElement | null;
  if (!svgShellNode || !svgSideViewNode) throw new Error("No SVG node found with id 'ShipBody'");

  adjustSvgShellPosition(svgShellNode);

  // 3. Add symbols
  const symbols = generateContainerSymbols<T>(
    containersSymbolsConfig,
    svgSizeRatio,
    overridenHeight,
  );

  const symbolsNames = Object.keys(symbols);
  const symbolsTarget = (svgNode.querySelector("defs") as SVGElement | null) || svgNode;
  symbolsNames.forEach((key) => {
    symbolsTarget.appendChild(symbols[key]);
  });

  const belowSvgGroupsByBay = new Map<IBayPattern, ISvgGroupAdvanced>();
  const paddingBoxes = scaled(450);

  // 4. Top bay numbers
  const drawTopBayNumbers = (blps: BaySizesAndCgs[]) =>
    createSvgTopBayNumbers({
      blps,
      lcgsBy20Bay,
      svgNode,
      xPos,
      fontColor: symbolsOptions && symbolsOptions.fontColor,
      fontSize: 32,
      onDoubleClick: bayNumbersDoubleClickAction,
    });

  if (bayLevelPositionsAbove.length) drawTopBayNumbers(bayLevelPositionsAbove);
  else drawTopBayNumbers(bayLevelPositionsBelow);

  let svgBayLevelGroups: ISvgGroup[] = [];
  if (totalSlotsCount > 0) {
    // 5. Vessel parts
    drawVesselParts(midVcg);

    // 6. Draw containers
    svgBayLevelGroups = drawSideContainers({
      bayLevelPositionsAbove,
      bayLevelPositionsBelow,
      calculatedPairedBays: pairedBays,
      offsetBottomBasesDict,
      containersWithPositions,
      overridenHeight,
    });

    // 7. Draw bulkheads
    drawBulkheads(xRange, midVcgByBay);
  }

  return {
    sideViewSvg: svgNode,
    missingImportantXcgs,
    xRange,
    yRange,
    svgSizeRatio,
    xPos,
    yPos,
    svgBayLevelGroups,
    symbols,
  };

  function adjustSvgShellPosition(svgShellNode: SVGElement) {
    const yScale =
      (scaled(midVcg) + svgElementMeasures.paddingShip.y) / svgElementMeasures.sideHeight;

    svgShellNode.style.transformOrigin = "0 0";
    svgShellNode.style.transformBox = "fill-box";
    svgShellNode.style.overflow = "visible";
    svgShellNode.style.transform = `matrix(1, 0, 0, ${yScale}, 0, ${
      svgElementMeasures.paddingShip.y + yPos(midVcg)
    })`;

    const svgBowTopNode = svgNode.querySelector("#BowTop") as SVGElement | null;
    if (!svgBowTopNode) throw new Error("No SVG node found with id 'BowTop'");
    svgBowTopNode.setAttribute(
      "transform",
      `matrix(1, 0, 0, 1, 0, ${
        -svgElementMeasures.translatedPartsY + svgElementMeasures.paddingShip.y + yPos(midVcg)
      })`,
    );

    // Adjust colors
    if (symbolsOptions?.shipTopColor) {
      const cTop = svgNode.querySelector("#_Side_Linear2") as SVGLinearGradientElement;

      if (cTop) {
        const offsets = Array.from(cTop.querySelectorAll("stop[offset]")) as SVGStopElement[];

        const color1 = symbolsOptions.shipTopColor;
        const color0 = colorShade(color1, -20);

        const off0 = offsets.find((o) => o.getAttribute("offset") === "0");
        const off1 = offsets.find((o) => o.getAttribute("offset") === "1");

        if (off0) off0.style.stopColor = color0;
        if (off1) off1.style.stopColor = color1;
      }
    }

    if (symbolsOptions?.shipBottomColor) {
      const cBottom = svgNode.querySelector("#_Side_Linear1") as SVGLinearGradientElement;

      if (cBottom) {
        const offsets = Array.from(cBottom.querySelectorAll("stop[offset]")) as SVGStopElement[];

        const color0 = symbolsOptions.shipBottomColor;
        const color1 = colorShade(color0, 60);

        const off0 = offsets.find((o) => o.getAttribute("offset") === "0");
        const off1 = offsets.find((o) => o.getAttribute("offset") === "1");

        if (off0) off0.style.stopColor = color0;
        if (off1) off1.style.stopColor = color1;
      }
    }
  }

  function drawSideContainers({
    bayLevelPositionsAbove,
    bayLevelPositionsBelow,
    calculatedPairedBays,
    offsetBottomBasesDict,
    containersWithPositions,
    overridenHeight,
  }: {
    bayLevelPositionsAbove: BaySizesAndCgs[];
    bayLevelPositionsBelow: BaySizesAndCgs[];
    calculatedPairedBays: IGetPairedBaysOutput;
    offsetBottomBasesDict: Record<string, number>;
    containersWithPositions: TGetContainersCgsResult;
    overridenHeight?: number;
  }): ISvgGroup[] {
    const svgGroups: ISvgGroup[] = [];
    const belowDeckBoxes: Array<
      {
        name: string;
        pointsInfo: Omit<ISvgGroupAdvanced, "svgGroup">;
        isAbove?: boolean;
        bbase: IBayPattern;
      }[]
    > = [[]];

    const svgGroupShipCovers = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svgGroupShipCovers.setAttribute("id", "ShipCovers");

    const svgGroupBays = document.createElementNS("http://www.w3.org/2000/svg", "g");

    svgGroupBays.setAttribute("id", "Bays");

    svgNode.appendChild(svgGroupShipCovers);

    const maxDistToDrawBoxes = scaled(4000); // 4 meters
    let lastXBelowDeckBox = Infinity;
    let lastLevel = BayLevelEnum.ABOVE;

    /** Get the filter function to use to filter the containers */
    const getContsFilterFn = obtainSideViewFilterFn(selectContainerCriteria);

    const filteredContainersByMaxSizePerStack = containersWithPositions.smartCGs.smartBayBlocks
      .map(getContsFilterFn)
      .filter((c) => c.containers.length > 0)
      .map((c) => ({
        ...c,
        containers: c.containers.map(convertContainerExtWCgsToCellInfo),
      }));

    type TContainersByBays = Record<
      `${string}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`,
      ICellCGsInfo[]
    >;

    const containersByBays = filteredContainersByMaxSizePerStack.reduce((acc, c) => {
      acc[`${c.tBaysStr}-${c.level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`] = c.containers;
      return acc;
    }, {} as TContainersByBays);

    const baysToIterate = [
      ...calculatedPairedBays.pairedBays,
      ...calculatedPairedBays.unpairedBays,
    ].sort((a, b) => (a.base === b.base ? a.level - b.level : Number(a.base) - Number(b.base)));

    baysToIterate.forEach((pb) => {
      const blps = (
        pb.level === BayLevelEnum.ABOVE ? bayLevelPositionsAbove : bayLevelPositionsBelow
      ).filter((blp) => pb.allBays.indexOf(blp.isoBay) >= 0);

      const isoBays = blps.map((b) => b.isoBay).filter((b, i, a) => a.indexOf(b) === i) as [
        IBayPattern,
        IBayPattern?,
      ];

      const level = blps[0].level;

      const svgGroupResp = createSvgGroup({
        isoBays,
        level,
        offsetBottomBasesDict,
        containersWithPositions,
      });

      if (!svgGroupResp) return;

      if (level === BayLevelEnum.BELOW) {
        belowSvgGroupsByBay.set(pb.base, svgGroupResp);
      }

      const midVcgOfThisBay = midVcgByBay[pb.base];
      const midVcgOfthisBayPosY = yPos(midVcgOfThisBay);

      if (svgGroupResp.yMax > midVcgOfthisBayPosY || level === BayLevelEnum.ABOVE) {
        if (lastXBelowDeckBox - svgGroupResp.xMax > maxDistToDrawBoxes || level !== lastLevel) {
          belowDeckBoxes.push([]);
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { svgGroup, ...pointsInfo } = svgGroupResp;

        belowDeckBoxes[belowDeckBoxes.length - 1].push({
          name: `${isoBays.join("_")}-${level}`,
          pointsInfo,
          isAbove: level === BayLevelEnum.ABOVE,
          bbase: pb.base,
        });

        lastXBelowDeckBox = svgGroupResp.xMin;
        lastLevel = level;
      }

      svgGroups.push(svgGroupResp);

      drawContainers({
        isoBays: isoBays as [IBayPattern, IBayPattern?],
        level,
        containersByBays,
        svgGroup: svgGroupResp.svgGroup,
        xMin: svgGroupResp.xMin,
        yMinForGroup: svgGroupResp.yMinForGroup,
        overridenHeight,
      });

      svgGroupBays.appendChild(svgGroupResp.svgGroup);
    });

    drawShipSpacesInShell();

    svgNode.appendChild(svgGroupBays);
    return svgGroups;

    /** Creates the SVG group for a single bay-and-level
     * and returns the SVG group, the xMin, xMax, yMin, yMax, yMinForGroup and adjustBB
     * for the boxes behind the containers
     */
    function createSvgGroup({
      isoBays,
      level,
      offsetBottomBasesDict,
      containersWithPositions,
    }: {
      isoBays: [IBayPattern, IBayPattern?];
      level: BayLevelEnum;
      offsetBottomBasesDict: Record<string, number>;
      containersWithPositions: TGetContainersCgsResult;
    }): ISvgGroupAdvanced | undefined {
      const isoBaseBay = isoBays[0];
      const isoPairedBay = isoBays[1];
      const levelBaseBay = level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW;
      const baysDuple = isoBays.join("-") as `${IBayPattern}-${IBayPattern}`;
      const stackBottomsByBay = containersWithPositions.stackBottomsByBay;

      const adjustBB = offsetBottomBasesDict[`${isoBays.join("-")}-${levelBaseBay}`] ?? 0;

      const stackBottomOfThisBay = stackBottomsByBay[`${isoBaseBay}-${levelBaseBay}`];
      const stackBottomOfPairedBay = isoPairedBay
        ? stackBottomsByBay[`${isoPairedBay}-${levelBaseBay}`]
        : undefined;

      const { bayLevelCgsInfo } = containersWithPositions;
      const bayLevelInfo = bayLevelCgsInfo[baysDuple]?.[levelBaseBay];
      const maxHeightAbsoluteOfBay = bayLevelInfo?.maxHeightAbsoluteOfBay;

      if (maxHeightAbsoluteOfBay === undefined) return undefined;
      const absoluteBottomBase = bayLevelInfo?.bottomBase || 0;

      const xMin = xPos(bayLevelInfo?.minAftLcg ?? 0, 0);
      const xMax = xPos(bayLevelInfo?.maxForeLcg ?? 0, 0);
      /** SVG Coordinate (lower at the top) */
      const yMax = roundDec(yPos(adjustBB + absoluteBottomBase), 0);
      /** SVG Coordinate (higher at the bottom) */
      const yMin = roundDec(yPos(adjustBB + maxHeightAbsoluteOfBay), 0);

      const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");

      svgGroup.classList.add("svg-group-bay-level");
      svgGroup.setAttribute("data-id", `${isoBays.join("-")}|${level}`);
      svgGroup.setAttribute("data-bbase", `${absoluteBottomBase}`);
      svgGroup.setAttribute("data-bay", isoBaseBay);
      svgGroup.setAttribute("data-height", `${maxHeightAbsoluteOfBay - absoluteBottomBase}`);
      svgGroup.setAttribute("data-size-w", `${Math.abs(roundDec(xMin - xMax))}`);
      svgGroup.setAttribute("data-size-h", `${Math.abs(roundDec(yMin - yMax))}`);

      if (missingImportantXcgs) svgGroup.setAttribute("data-action", `move`);

      svgGroup.setAttribute(
        "transform",
        `translate(${xMin}, ${roundDec(yMin + svgElementMeasures.paddingShip.y)})`,
      );

      const pTA = { x: xMin, y: yMin };
      const pTF = { x: xMax, y: yMin };
      let pBA = { x: xMin, y: yMax };
      let pBF = { x: xMax, y: yMax };

      if (
        stackBottomOfThisBay !== undefined &&
        stackBottomOfPairedBay !== undefined &&
        stackBottomOfThisBay !== stackBottomOfPairedBay
      ) {
        pBA = { x: xMin, y: roundDec(yPos(adjustBB + stackBottomOfPairedBay), 0) };
        pBF = { x: xMax, y: roundDec(yPos(adjustBB + stackBottomOfThisBay), 0) };
      }

      return {
        svgGroup,

        pTA,
        pTF,
        pBA,
        pBF,

        xMin,
        xMax,
        yMin,
        yMax,
        yMinForGroup: yMin,

        adjustBB: scaled(adjustBB),
      };
    }

    /** Draws the containers of a single bay-and-level */
    function drawContainers({
      isoBays,
      level,
      svgGroup,
      xMin,
      yMinForGroup,
      containersByBays,
      overridenHeight,
    }: {
      isoBays: [IBayPattern, IBayPattern?];
      level: BayLevelEnum;
      svgGroup: SVGGElement;
      xMin: number;
      yMinForGroup: number;
      containersByBays: TContainersByBays;
      overridenHeight?: number;
    }): void {
      const key = `${isoBays.filter(Boolean).join("-")}-${level}` as `${string}-${
        BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`;

      const allConts = containersByBays[key];
      if (!allConts) return;

      allConts
        .sort((a, b) => Number(a.meta?.row || 0) - Number(b.meta?.row || 0))
        .forEach((c) => {
          if (
            c.lcg === undefined ||
            c.bottomBaseWithSeparation === undefined ||
            c.meta === undefined
          )
            return;

          const cellPos = `${c.meta.row}${c.meta.tier}`;
          const symbolName = containersSymbolsConfig.nameFn(
            containersSymbolsConfig.prefixSymbol,
            containersSymbolsConfig.getItem(c.meta.pk),
            c.extending || 0,
          );

          if (symbolName) {
            const contSvg = drawContainerSvg(
              c,
              offsetBottomBasesDict,
              isoBays,
              level,
              xMin,
              yMinForGroup,
              cellPos,
              symbolName,
              overridenHeight,
            );

            if (contSvg) svgGroup.appendChild(contSvg);
          }
        });

      svgNode.appendChild(svgGroup);
    }

    function drawShipSpacesInShell() {
      belowDeckBoxes
        .filter((b) => b.length > 0)
        .forEach((b) => {
          const pathParts: string[] = [];

          b.forEach((bb) => {
            const isBelow = !bb.isAbove;
            const { pTA, pTF, pBA, pBF, adjustBB } = bb.pointsInfo;

            // Create the coordinates for the box, using the points p(AF)
            const leftTopPoint = { x: pTA.x - paddingBoxes, y: pTA.y - paddingBoxes };
            const rightTopPoint = { x: pTF.x + paddingBoxes, y: pTF.y - paddingBoxes };
            const leftBottomPoint = { x: pBA.x - paddingBoxes, y: pBA.y + paddingBoxes - adjustBB };
            const rightBottomPoint = {
              x: pBF.x + paddingBoxes,
              y: pBF.y + paddingBoxes - adjustBB,
            };

            if (isBelow) {
              const midVcgOfThisBay = midVcgByBay[bb.bbase] || midVcg;
              const midVcgOfthisBayPosY = roundDec(
                yPos(midVcgOfThisBay) - adjustBB / 2 - paddingBoxes / 2,
                0,
              );
              leftTopPoint.y = midVcgOfthisBayPosY + paddingBoxes;
              rightTopPoint.y = midVcgOfthisBayPosY + paddingBoxes;
            }

            pathParts.push(
              `M${roundDec(leftBottomPoint.x, 0)},${roundDec(leftBottomPoint.y, 0)}`,
              `L${roundDec(leftTopPoint.x, 0)},${roundDec(leftTopPoint.y, 0)}`,
              `L${roundDec(rightTopPoint.x, 0)},${roundDec(rightTopPoint.y, 0)}`,
              `L${roundDec(rightBottomPoint.x, 0)},${roundDec(rightBottomPoint.y, 0)}`,
            );

            const rbpy = roundDec(rightBottomPoint.y, 0);
            const lbpb = roundDec(leftBottomPoint.y, 0);

            if (rbpy !== lbpb) {
              const midX = roundDec((leftBottomPoint.x + rightBottomPoint.x) / 2, 0);
              const paddingLower = lbpb > rbpy ? paddingBoxes : -paddingBoxes;
              pathParts.push(`L${midX + paddingLower},${rbpy}`);
              pathParts.push(`L${midX + paddingLower},${lbpb}`);
            }
          });

          const svgPath = document.createElementNS("http://www.w3.org/2000/svg", "path");

          svgPath.setAttribute("stroke", symbolsOptions?.strokeColor || "red");
          svgPath.setAttribute("fill", symbolsOptions?.skyColor || "white");
          svgPath.setAttribute("stroke-width", "0");
          svgPath.setAttribute("name", `belowDeckBox`);
          svgPath.setAttribute("data-n", `${b[0].name}`);

          svgPath.setAttribute("d", `${pathParts.join(" ")} Z`);
          svgPath.setAttribute("transform", `translate(0, ${svgElementMeasures.paddingShip.y})`);

          svgGroupShipCovers.appendChild(svgPath);
        });
    }

    function drawContainerSvg(
      c: ICellCGsInfo,
      offsetBottomBasesDict: Record<string, number>,
      isoBays: [IBayPattern, IBayPattern?],
      level: BayLevelEnum,
      xMin: number,
      yMinForGroup: number,
      cellPos: string,
      symbolName: string,
      overridenHeight: number | undefined,
    ) {
      const contSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");
      contSvg.setAttribute("href", `#${symbolName}`);
      contSvg.setAttribute("pk", c.meta?.pk || "");

      const adjustBB = offsetBottomBasesDict[`${isoBays.filter(Boolean).join("-")}-${level}`] ?? 0;

      if (c.lcg === undefined) {
        return;
      }

      const bb =
        overridenHeight && c.containersBelowCount !== undefined
          ? c.stackBottomBase + c.containersBelowCount * overridenHeight
          : c.bottomBaseWithSeparation || 0;

      contSvg.setAttribute("x", `${roundDec(xPos(c.lcg) - xMin, 0)}`);
      contSvg.setAttribute("y", `${roundDec(yPos(bb + adjustBB) - yMinForGroup, 0)}`);

      contSvg.setAttribute("data-p", `${cellPos}`);
      contSvg.setAttribute("data-pk", `${c.meta?.pk}`);
      contSvg.setAttribute("data-bb", `${c.bottomBaseWithSeparation}-${c.tierBottomBase}`);

      contSvg.setAttribute("data-bay", c.isoOddBay);

      const contTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");

      contTitle.innerHTML = `${c.isoOddBay}-${cellPos}`;

      contSvg.appendChild(contTitle);
      return contSvg;
    }
  }

  function drawBulkheads(xRange: number, midVcgByBay: Record<string, number>): void {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

    path.setAttribute("data-type", "bulkhead");
    path.setAttribute("stroke", "#444");
    path.setAttribute("fill", "#fff");
    path.setAttribute("line-width", "4");
    path.setAttribute("stroke-width", String(roundDec((symbolsOptions?.strokeWidth || 8) / 2, 0)));
    path.setAttribute("fill-opacity", "1");

    const pathParts: string[] = [];

    bayLevelPositionsBelow
      .filter((m) => m.bulkhead && (m.bulkhead.fore || m.bulkhead.aft))
      .forEach((maxByTier) => {
        const isoBaseBay = maxByTier.isoBay;
        const midVcg = midVcgByBay[isoBaseBay];

        if (maxByTier.bulkhead) {
          const hasFore = maxByTier.bulkhead.fore;
          const hasAft = maxByTier.bulkhead.aft;

          const foreLcg = lcgsBy20Bay[maxByTier.isoBay].maxForeLcg;
          const aftLcg = lcgsBy20Bay[maxByTier.isoBay].minAftLcg;

          const belowSvgGroup = belowSvgGroupsByBay.get(isoBaseBay);
          const h = belowSvgGroup ? belowSvgGroup.yMax + paddingBoxes * 3 : scaled(midVcg);

          if (hasFore) {
            addBulkSvgNode(foreLcg, midVcg, h);
          }

          if (hasAft) {
            addBulkSvgNode(aftLcg, midVcg, h);
          }
        }
      });

    function addBulkSvgNode(lcg: number | undefined, maxV: number | undefined, heightSvg: number) {
      const BULK_WIDTH = 6;
      if (!lcg || !maxV) return;

      const x = xPos(lcg);

      // Removes bugs when the bulkhead is outside the ship
      if (!xRange || Math.abs(x / xRange) > 1.5) return;

      pathParts.push(
        `M${xPos(lcg)},${yPos(maxV) + svgElementMeasures.paddingShip.y} h${BULK_WIDTH} V${
          heightSvg + svgElementMeasures.paddingShip.y
        } h-${BULK_WIDTH} z`,
      );
    }

    path.setAttribute("d", pathParts.join(" "));
    svgNode.appendChild(path);
  }

  function drawVesselParts(midVcg: number): void {
    const midY =
      -svgElementMeasures.translatedPartsY + svgElementMeasures.paddingShip.y + yPos(midVcg);

    vOneBaySlots.forEach((vOneBay) => {
      if (vOneBay.type === VesselPartTypeEnum.BAY || vOneBay.type === VesselPartTypeEnum.SPACER) {
        return;
      }

      const x = xPos(vOneBay.aftLcg, 2);

      switch (vOneBay.type) {
        case "BRG":
          {
            const aboveDeckHeight = scaled(maxHeight !== undefined ? maxHeight - midVcg : 0) * 1.65;

            const yScale = aboveDeckHeight / svgElementMeasures.parts.bridge.width;

            useSymbolAndTransform({
              id: `#_Side_Bridge`,
              x: x,
              y: midY + svgElementMeasures.parts.bridge.width - aboveDeckHeight,
              padX: scaled(1000), // 250mm = 0.25m L space between parts
              padY: 0,
              len: scaled(feetToMillimeters(vOneBay.len)),
              yScale,
              objToAppend: svgNode,
              partNameInMeasures: "bridge",
            });
          }
          break;

        case "SMK":
          {
            const aboveDeckHeight = scaled(maxHeight !== undefined ? maxHeight - midVcg : 0) * 1.2;

            const yScale = aboveDeckHeight / svgElementMeasures.parts.smokestack.width;

            useSymbolAndTransform({
              id: `#_Side_SmokeStack`,
              x: x,
              y: midY + svgElementMeasures.parts.smokestack.width - aboveDeckHeight,
              padX: scaled(250), // 250mm = 0.25m L space between parts
              padY: 0,
              len: scaled(feetToMillimeters(vOneBay.len)),
              yScale,
              objToAppend: svgNode,
              partNameInMeasures: "smokestack",
            });
          }
          break;
        case "CRN":
          {
            const aboveDeckHeight = scaled(maxHeight !== undefined ? maxHeight - midVcg : 0) * 1.0;

            const yScale = aboveDeckHeight / svgElementMeasures.parts.crane.width;

            const spaceBetweenCranes = scaled(1500);
            const allSpaceBetweenCranes = spaceBetweenCranes * (vOneBay.cranes.length - 1) * 0.5;

            vOneBay.cranes
              .slice()
              .sort()
              .forEach((crane, idx) => {
                const attrs = {
                  x: x - allSpaceBetweenCranes + idx * spaceBetweenCranes,
                  y: midY + svgElementMeasures.parts.crane.width - aboveDeckHeight,

                  name: crane === CraneSideEnum.PORT ? "port" : "stbd",
                };

                useSymbolAndTransform({
                  id: crane === CraneSideEnum.PORT ? `#_Side_CraneFront` : `#_Side_CraneBack`,
                  ...attrs,
                  transformOrigin: "0 0",
                  padX: scaled(250), // 250mm = 0.25m L space between parts
                  padY: 0,
                  len: scaled(feetToMillimeters(vOneBay.len)),
                  yScale,
                  objToAppend: svgNode,
                  partNameInMeasures: "crane",
                });
              });
          }

          break;
      }
    });
  }

  function useSymbolAndTransform({
    id,
    x,
    y,
    padX,
    padY,
    len,
    yScale,
    objToAppend,
    transformOrigin = "0 0",
    name,
    partNameInMeasures,
  }: {
    id: string;
    x: number;
    y: number;
    padY: number;
    padX: number;
    len: number;
    yScale: number;
    objToAppend: SVGElement;
    transformOrigin?: string;
    name?: string;
    partNameInMeasures: keyof IReturnTypeGetMeasuresFromAttrs["parts"];
  }): void {
    const elDef = svgElement.querySelector(id) as SVGElement;
    if (!elDef) throw new Error(`No SVG node found with id '${id}'`);

    const el = elDef.querySelector("g")?.cloneNode(true) as SVGGElement | undefined;
    if (!el) throw new Error(`No SVG node G found within id '${id}'`);

    const xScale = (len - padX * 2) / svgElementMeasures.parts[partNameInMeasures].length;

    const adjustedZScale =
      (svgElementMeasures.parts[partNameInMeasures].width /
        (svgElementMeasures.parts[partNameInMeasures].width + svgElementMeasures.deckPadWidth)) *
      yScale;

    el.style.transformOrigin = transformOrigin;
    el.style.transformBox = "fill-box";
    el.style.overflow = "visible";

    el.style.transform = `matrix(${xScale}, 0, 0, ${adjustedZScale}, ${x + padX}, ${y + padY})`;

    if (name) {
      el.setAttribute("data-name", name);
    }

    objToAppend.appendChild(el);
  }
}

export interface ICreateSimpleSideViewProps<T> {
  sizeSummary: ISizeSummary;
  baysData: IBayLevelData[];
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  symbolsOptions?: IGenerateContainerSymbolsProps;
  vesselPartsData?: IVesselParts[];
  offsetBottomBases?: Array<IBayLevelOffsetBottomBase>;
  containersWithPositions: TGetContainersCgsResult;
  selectContainerCriteria: TSelectContainerCriteria;
  bayNumbersDoubleClickAction?: (ev: SVGElement | HTMLElement) => void;
  svgElement: SVGElement;
  svgElementMeasures: IReturnTypeGetMeasuresFromAttrs;
  containersSymbolsConfig: TContainersSymbolsConfig<T>;
  overridenHeight?: number;
}

export type TSelectContainerCriteria =
  "stbdOuter" | "portOuter" | "all" | "allSbtdSide" | "allPortSide" | "maxSize" | IRowPattern;

export interface IBayAboveBelowLVcgs {
  [name: string]: {
    nearestAboveVcg?: number;
    nearestBelowVcg?: number;
    bottomBase?: number;
    foreLcg: number;
    aftLcg: number;
    lcg: number;
    maxSize: number;
    pairedBay?: ForeAftEnum;
    b20?: {
      fwdLPoint: number;
      aftLPoint: number;
    };
  };
}

interface ISvgGroup {
  svgGroup: SVGGElement;
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
  yMinForGroup: number;
  adjustBB: number;
}

interface ISvgGroupAdvanced {
  svgGroup: SVGGElement;
  pTA: { x: number; y: number };
  pTF: { x: number; y: number };
  pBA: { x: number; y: number };
  pBF: { x: number; y: number };
  yMin: number;
  xMin: number;
  xMax: number;
  yMax: number;
  yMinForGroup: number;
  adjustBB: number;
}
